import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { generatePassword } from "../lib/utils";
import { toast } from "sonner";
import { Target, Home, Users, Trophy, History, Swords, Plus, LogOut, Shield, User, Dumbbell, Rss, RefreshCw, KeyRound, X } from "lucide-react";

const nav = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/feed", label: "Feed", icon: Rss },
  { to: "/players", label: "Players", icon: Users },
  { to: "/leaderboards", label: "Leaders", icon: Trophy },
  { to: "/head-to-head", label: "Rivalry", icon: Swords },
  { to: "/practice", label: "Practice", icon: Dumbbell },
  { to: "/history", label: "History", icon: History },
  { to: "/club", label: "Club", icon: Shield },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = user?.linked_player_id
    ? [nav[0], { to: `/players/${user.linked_player_id}`, label: "My Stats", icon: User }, ...nav.slice(1)]
    : nav;

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain">
      <header className="sticky top-0 z-40 border-b border-[#262626] bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <button
            data-testid="logo-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-[#FF3B30] rounded-sm">
              <Target className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-extrabold tracking-tight uppercase text-white">
              Oche<span className="text-[#FF3B30]">One</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-0.5 overflow-x-auto flex-1 mx-3 justify-center">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold uppercase tracking-wide rounded-sm transition-colors duration-150 whitespace-nowrap ${
                    isActive
                      ? "text-white bg-[#1a1a1a]"
                      : "text-slate-400 hover:text-white hover:bg-[#141414]"
                  }`
                }
              >
                <n.icon className="w-4 h-4 shrink-0" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="new-match-header-btn"
              onClick={() => navigate("/new")}
              className="flex items-center gap-1.5 px-3 md:px-4 py-2 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-bold uppercase tracking-wide rounded-sm transition-colors duration-150"
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              <span className="hidden sm:inline">New Match</span>
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              title={user?.email}
              className="p-2 text-slate-400 hover:text-[#FF3B30] transition-colors duration-150"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="md:hidden flex items-center justify-between px-2 pb-2 gap-1 overflow-x-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-bold uppercase rounded-sm ${
                  isActive ? "text-white bg-[#1a1a1a]" : "text-slate-500"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">{children}</main>
      <PasswordSetupPrompt />
    </div>
  );
}

function PasswordSetupPrompt() {
  const { user, setInitialPassword, keepCurrentPassword } = useAuth();
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || !user.must_change_password || dismissed) return null;

  const save = async () => {
    if (pwd.trim().length < 4) return toast.error("Password must be at least 4 characters");
    setBusy(true);
    try {
      await setInitialPassword(pwd.trim());
      toast.success("Password updated — use it next time you log in");
    } catch {
      toast.error("Could not update password");
      setBusy(false);
    }
  };

  const keep = async () => {
    setDismissed(true);
    try {
      await keepCurrentPassword();
    } catch {}
  };

  return (
    <div data-testid="password-setup-prompt" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="panel rounded-sm p-6 w-full max-w-md bg-[#111]">
        <div className="flex items-center justify-between mb-1">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-white">
            <KeyRound className="w-5 h-5 text-[#34C759]" /> Set Your Password
          </h2>
          <button data-testid="pw-prompt-close" onClick={keep} className="text-slate-500 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-slate-400 text-sm mb-4">
          You're signed in with a password set by your league owner. Want to set your own? You can also keep the current one — we won't ask again.
        </p>
        <div className="flex gap-2">
          <input
            data-testid="pw-prompt-input"
            type="text"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="New password (min 4 chars)"
            className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#34C759]"
          />
          <button
            type="button"
            data-testid="pw-prompt-generate"
            onClick={() => setPwd(generatePassword())}
            className="flex items-center gap-1.5 px-3 py-3 panel rounded-sm text-slate-200 hover:text-[#34C759] hover:border-[#34C759] transition-colors whitespace-nowrap font-bold uppercase text-xs tracking-wide"
          >
            <RefreshCw className="w-4 h-4" /> Generate
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button
            data-testid="pw-prompt-save"
            onClick={save}
            disabled={busy}
            className="flex-1 bg-[#34C759] hover:bg-[#2fb350] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 rounded-sm transition-colors"
          >
            {busy ? "Saving..." : "Save New Password"}
          </button>
          <button
            data-testid="pw-prompt-keep"
            onClick={keep}
            className="flex-1 panel rounded-sm py-3 font-bold uppercase tracking-wide text-slate-300 hover:text-white transition-colors"
          >
            Keep Current
          </button>
        </div>
      </div>
    </div>
  );
}
